<template>
    <div class="nav-item-wrapper">
        <router-link :to="to" class="nav-item-link">
            <component :is="iconComponent" class="nav-icon" />
            <span class="nav-tooltip">{{ title }}</span>
        </router-link>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import {
    TeamOutlined,
    BankOutlined,
    UserOutlined,
    DatabaseOutlined,
    BarsOutlined,
    ForkOutlined,
    StockOutlined,
    ClusterOutlined,
    BellOutlined,
    ShoppingOutlined,
    TagsOutlined,
    StopOutlined,
    ExperimentOutlined
} from '@ant-design/icons-vue';

export default defineComponent({
    name: 'SidebarNavItem',
    components: {
        TeamOutlined,
        BankOutlined,
        UserOutlined,
        DatabaseOutlined,
        BarsOutlined,
        ForkOutlined,
        StockOutlined,
        ClusterOutlined,
        BellOutlined,
        ShoppingOutlined,
        TagsOutlined,
        StopOutlined,
        ExperimentOutlined
    },
    props: {
        to: {
            type: String,
            required: true
        },
        icon: {
            type: String,
            required: true,
            validator: (value: string) => {
                return ['team', 'bank', 'user', 'database', 'bars', 'fork', 'stock', 'cluster', 'bell', 'shop', 'tags', 'stop', 'experiment'].includes(value);
            }
        },
        title: {
            type: String,
            required: true
        }
    },
    setup(props) {
        const iconComponent = computed(() => {
            const iconMap: Record<string, string> = {
                team: 'TeamOutlined',
                bank: 'BankOutlined',
                user: 'UserOutlined',
                database: 'DatabaseOutlined',
                bars: 'BarsOutlined',
                fork: 'ForkOutlined',
                stock: 'StockOutlined',
                cluster: 'ClusterOutlined',
                bell: 'BellOutlined',
                shop: 'ShopOutlined',
                tags: 'TagsOutlined',
                stop: 'StopOutlined',
                experiment: 'ExperimentOutlined'
            };
            return iconMap[props.icon];
        });

        return {
            iconComponent
        };
    }
});
</script>

<style lang="scss" scoped>
.nav-item-wrapper {
    padding: 6px 16px;
}

.nav-item-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    text-decoration: none;
    background: transparent;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;

    .nav-icon {
        color: #86868b;
        font-size: 22px;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .nav-tooltip {
        position: absolute;
        left: 64px;
        background: #1d1d1f;
        color: white;
        padding: 8px 12px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        letter-spacing: -0.01em;
        white-space: nowrap;
        opacity: 0;
        visibility: hidden;
        transform: translateX(-4px);
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        pointer-events: none;
        box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
        z-index: 1000;

        &::before {
            content: '';
            position: absolute;
            left: -4px;
            top: 50%;
            transform: translateY(-50%);
            width: 0;
            height: 0;
            border-style: solid;
            border-width: 5px 5px 5px 0;
            border-color: transparent #1d1d1f transparent transparent;
        }
    }

    &:hover {
        background: #f5f5f7;

        .nav-icon {
            color: #1d1d1f;
            transform: scale(1.1);
        }

        .nav-tooltip {
            opacity: 1;
            visibility: visible;
            transform: translateX(0);
        }
    }

    &.router-link-active {
        background: #007aff;

        .nav-icon {
            color: white;
        }

        &::before {
            content: '';
            position: absolute;
            left: -16px;
            top: 50%;
            transform: translateY(-50%);
            width: 4px;
            height: 32px;
            background: #007aff;
            border-radius: 0 4px 4px 0;
        }
    }

    &.router-link-active:hover {
        background: #0051d5;

        .nav-tooltip {
            background: #007aff;

            &::before {
                border-color: transparent #007aff transparent transparent;
            }
        }
    }
}
</style>
